import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Line } from "react-chartjs-2";
import Image from "next/image";
import Close from "/public/assets/img/pop-icon/close-button.png";
import Target from "/public/assets/img/pop-icon/target.png";
import Status from "/public/assets/img/pop-icon/signal-status.png";
import Explore from "/public/assets/img/pop-icon/explore.png";
import Sitting from "/public/assets/img/pop-icon/sitting.png";

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  Filler,
  ChartDataLabels
);

export default function ResultModal({
  predictedRanks,
  showResultModal,
  setShowResultModal,
  examLabel = "NEET",
  showCollegePredictorCta = true,
}) {
  useEffect(() => {
    if (showResultModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showResultModal]);

  if (!showResultModal || !predictedRanks || predictedRanks.length === 0) {
    return null;
  }

  const rankInfo = predictedRanks[0];
  const historicalData = rankInfo.previousYearRanks || {};

  const years = Object.keys(historicalData);
  const ranks = years.map((year) => Number(historicalData[year]));

  const maxRank = ranks.length > 0 ? Math.max(...ranks) : 1000;

  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Predicted Rank",
        data: ranks,
        borderColor: "#FF9245",
        backgroundColor: "rgba(255, 146, 69, 0.2)",
        fill: true,
        pointBackgroundColor: "#FF9245",
        pointBorderColor: "#FF9245",
        pointRadius: 5,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0.35,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 10,
        },
      },

      tooltip: {
        enabled: true,
      },

      datalabels: {
        display: true,
        color: "#000",
        backgroundColor: "#ffffff",
        borderColor: "#FF9245",
        borderWidth: 1,
        borderRadius: 6,
        padding: {
          top: 4,
          bottom: 4,
          left: 6,
          right: 6,
        },
        font: {
          weight: "bold",
          size: 10,
        },
        anchor: "end",
        align: "bottom",
        offset: 6,
        formatter: function (value) {
          return value;
        },
      },
    },

    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: maxRank + 1000,
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        title: {
          display: true,
          text: "Rank",
          font: {
            size: 12,
            weight: "bold",
          },
          color: "#097F78",
          padding: {
            top: 0,
            left: 30,
            right: 0,
            bottom: 0,
          },
        },
        ticks: {
          padding: 15,
          color: "#000",
          font: {
            weight: "bold",
          },
        },
      },

      x: {
        offset: true,
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        title: {
          display: true,
          text: "Year",
          font: {
            size: 12,
            weight: "bold",
          },
          color: "#097F78",
        },
        ticks: {
          color: "#000",
          font: {
            weight: "bold",
          },
        },
      },
    },

    layout: {
      padding: {
        top: 25,
        left: 0,
        right: 25,
        bottom: 0,
      },
    },
  };

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 overflow-hidden">
      <div className="bg-white p-4 md:p-8 rounded-2xl w-[95%] md:w-[80%] max-w-2xl text-center border-[1px] shadow-lg mt-28 md:mt-32 max-h-[90vh] overflow-y-auto scrollbar-hide">
        <div className="flex justify-end">
          <button
            onClick={() => setShowResultModal(false)}
            className="text-[#097F78] hover:text-gray-700 focus:outline-none"
          >
            <Image src={Close} width={24} height={24} alt="Close Icon" />
          </button>
        </div>

        <h2 className="text-sm md:text-lg font-semibold text-[#005f59]">
          Your Predicted {examLabel} Rank 2026
        </h2>

        <div className="mt-6">
          <div className="flex flex-col gap-2 text-left">
            <div className="flex items-center gap-2">
              <Image src={Target} width={24} height={24} alt="Target Icon" />
              <span className="text-gray-500 text-sm md:text-lg font-medium">
                {examLabel} Score:
              </span>
              <span className="font-semibold text-sm md:text-lg">
                {rankInfo.marks}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Image src={Status} width={24} height={24} alt="Status Icon" />
              <span className="text-gray-500 text-sm md:text-lg font-medium">
                Predicted Rank:
              </span>
              <span className="font-semibold text-sm md:text-lg text-[#B7804A]">
                {rankInfo.predictedRank}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Image src={Explore} width={24} height={24} alt="Explore Icon" />
              <span className="text-gray-500 text-sm md:text-lg font-medium">
                Confidence Range:
              </span>
              <span className="font-semibold text-sm md:text-lg">
                {rankInfo.confidenceRange}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-[15px] font-semibold text-sm md:text-lg text-[#005f59]">
            Historical Rank Comparison
          </h3>

          <div className="mt-4 sm:h-[300px] h-[220px]" style={{ width: "100%" }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        <div className="mt-1 flex justify-center items-center">
          <Image src={Sitting} width={48} height={48} alt="Sitting Icon" />
          <p className="text-xs md:text-sm font-semibold text-gray-600 w-1/2">
          {examLabel === "NEET PG"
            ? "Use your estimated rank to plan your MD/MS counselling journey."
            : "NEET Support helps you find the best medical college for you."}
          </p>
        </div>

        {showCollegePredictorCta && (
          <div className="mt-6 flex justify-center">
            <button
              className="bg-[#FF9245] hover:bg-[#fff] hover:ring-1 hover:ring-[#ff9245] hover:!text-[#ff9245] text-white font-semibold px-6 py-2 rounded-3xl shadow transition-colors duration-200"
              onClick={() => {
                window.location.href = "/neet-college-predictor";
              }}
            >
              Try College Predictor
            </button>
          </div>
        )}
      </div>
    </div>,
    typeof window !== "undefined" ? document.body : null
  );
}
